// /hr/vyplaty — HR prehled vyplat / fakturace (owner only).
//
// Seznam vsech osob za mesic: spoluprace, sazba, treninky, bonusy, celkova castka.
// Rozklik -> detail s rozpadem treninku/bonusu + moznost strhnout motivacni slozku sazby
// trenera (povinny duvod, trener ho uvidi). Prepinac mesicu od cervna 2026 po aktualni.

import { confirmDialog } from "../../components/confirmHost.jsx";
import { toast } from "../../components/Toast.jsx";
import React, { useEffect, useState } from "react";
import { Receipt, RefreshCw, X, Users, Coins, TrendingUp, Dumbbell, AlertTriangle, Ban, RotateCcw, Loader2, Plus, Download, Eye, EyeOff, Pencil, Check } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { ListovaniMesice, DlazdiceFiltr, Zapinatko, EmptyState, SkeletonRows, ErrorNote, Modal, HlavickaOkna, OdznakOkna } from "../../components/ui.jsx";
import { Pilulka } from "../../components/PrehledDlazdice.jsx";
import { apiFetch, useApiData } from "../../lib/api.js";
import InfoTip from "../../components/InfoTip.jsx";
import { formatDate } from "../../lib/format.js";

const FIRST_MONTH = "2026-06";
const MONTHS_CS = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];
function mesicLabel(m) {
  if (!m || !/^\d{4}-\d{2}$/.test(m)) return m || "—";
  const [y, mm] = m.split("-");
  return `${MONTHS_CS[parseInt(mm, 10) - 1]} ${y}`;
}
function fmt(n) { return new Intl.NumberFormat("cs-CZ").format(Math.round(n || 0)); }
function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function addMonth(m, delta) {
  const [y, mm] = m.split("-").map(Number);
  const d = new Date(y, mm - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
// Poslední UZAVŘENÝ měsíc = předchozí kalendářní měsíc (aktuální ještě běží), min. FIRST_MONTH.
function lastClosedMonth() {
  const prev = addMonth(currentMonth(), -1);
  return prev < FIRST_MONTH ? FIRST_MONTH : prev;
}

export default function HRVyplaty() {
  const [mesic, setMesic] = useState(lastClosedMonth());
  const { data, loading, refetch, refreshing } = useApiData(`/api/hr-vyplaty?mesic=${mesic}`);
  const [detailId, setDetailId] = useState(null);

  const rows = data?.rows || [];
  const [filter, setFilter] = useState("vse"); // vse | treneri | ostatni
  // 24.8.2026 (hlášení z /feedback): ukončený nebo bývalý s nulovou odměnou
  // sem už nepatří, jen zabírá místo. Přepínačem se dá dočasně zobrazit.
  const [zobrazitUkoncene, setZobrazitUkoncene] = useState(false);
  // Head Coach Senior = manažer, nepočítá se mezi trenéry (Head Coach Junior ano).
  const isTrener = (r) => r.is_trener && r.pozice !== "Head Coach Senior";
  const jeUkonceny = (r) => /ukon|b[yý]val/i.test(String(r.status || ""));
  const jeSkrytelny = (r) => jeUkonceny(r) && Math.round(Number(r.celkem) || 0) === 0;
  const ukoncenychSNulou = rows.filter(jeSkrytelny).length;
  const viditelneRows = zobrazitUkoncene ? rows : rows.filter((r) => !jeSkrytelny(r));
  const filteredRows = filter === "treneri" ? viditelneRows.filter(isTrener)
    : filter === "ostatni" ? viditelneRows.filter((r) => !isTrener(r)) : viditelneRows;
  const summary = {
    pocetOsob: filteredRows.length,
    celkemKVyplaceni: filteredRows.reduce((s, r) => s + (r.k_fakturaci ?? r.celkem), 0),
    treneri: filteredRows.filter(isTrener).length,
    zamestnanci: filteredRows.filter((r) => !isTrener(r)).length,
  };

  const zverejneno = data?.zverejneno || false;
  const dppExport = data?.dppExport || [];

  async function toggleZverejneno() {
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action: "set-zverejneno", mesic, zverejneno: !zverejneno }) });
      if (!res.ok) { const j = await res.json(); throw new Error(j.error || res.status); }
      refetch();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
  }

  function exportDpp() {
    if (dppExport.length === 0) { toast("Žádné DPP za tento měsíc.", "error"); return; }
    const hodinSlovo = (n) => { const a = Math.round(n); return a === 1 ? "hodina" : (a >= 2 && a <= 4 ? "hodiny" : "hodin"); };
    const lines = [`DPP ${mesicLabel(mesic)}`, ""];
    for (const d of dppExport) {
      if (d.total === 0) { lines.push(`- ${d.name} - 0 Kč`); continue; }
      if (d.bonusAmount > 0) {
        lines.push(`- ${d.name} - ${fmt(d.hoursAmount)} Kč (${d.hours} ${hodinSlovo(d.hours)}) + ${fmt(d.bonusAmount)} Kč bonus = ${fmt(d.total)} Kč celkem`);
      } else {
        lines.push(`- ${d.name} - ${fmt(d.total)} Kč${d.hours ? ` (${d.hours} ${hodinSlovo(d.hours)})` : ""}`);
      }
    }
    lines.push("", `Celkem DPP: ${fmt(dppExport.reduce((s, d) => s + d.total, 0))} Kč`);
    const blob = new Blob([lines.join("\n")], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `DPP-${mesic}.txt`; a.click();
    URL.revokeObjectURL(url);
  }

  async function toggleSchvaleno(personId, current) {
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action: "set-schvaleno", personId, mesic, schvaleno: !current }) });
      if (!res.ok) { const j = await res.json(); throw new Error(j.error || res.status); }
      refetch();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
  }

  async function toggleFaktura(personId, current) {
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action: "set-faktura", personId, mesic, faktura: !current }) });
      if (!res.ok) { const j = await res.json(); throw new Error(j.error || res.status); }
      refetch();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
  }

  const cur = currentMonth();
  const canPrev = mesic > FIRST_MONTH;
  const canNext = mesic < cur;
  // Predikce (13.7.2026): jen pro aktuální měsíc — odhad fakturace na konci
  // měsíce (odtrénováno + naplánované tréninky v Reserviu × sazba).
  const jePredikce = mesic === cur;

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4 px-4 py-4 sm:space-y-6 sm:px-5 sm:py-6">
      {/* Header */}
      <GradientHeader
        title="Výplaty a fakturace"
        icon={Receipt}
        loading={loading}
        akce={[
          { klic: "dpp", ikona: Download, popis: "Stáhnout DPP report pro účetní", onClick: exportDpp },
          { klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: loading || refreshing },
        ]}
      />

      {/* 12.9.2026 (fáze 4c): listování měsíci vypadlo z tmavé hlavičky ven na
          stránku. Hlavička nese název a akce, období je ovládání obsahu, stejně
          jako v Operations u Time trackingu. */}
      <ListovaniMesice
        popis={mesicLabel(mesic)}
        onZpet={() => canPrev && setMesic(addMonth(mesic, -1))}
        onVpred={() => canNext && setMesic(addMonth(mesic, 1))}
        zpetVypnuto={!canPrev}
        vpredVypnuto={!canNext}
        naDnesek={mesic !== lastClosedMonth() ? () => setMesic(lastClosedMonth()) : undefined}
        popisDneska="Na poslední uzavřený"
      />

      {/* Čísla nahoře zároveň filtrují podle typu (rozhodnutí Č2). */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <DlazdiceFiltr
          popisek="Osob"
          hodnota={summary.pocetOsob}
          ikona={Users}
          ton="neutralni"
          zapnuto={filter === "vse"}
          onClick={() => setFilter("vse")}
        />
        <DlazdiceFiltr
          popisek="Celkem k výplatě a fakturaci"
          hodnota={`${fmt(summary.celkemKVyplaceni)} Kč`}
          ikona={Coins}
          ton="znacka"
        />
        <DlazdiceFiltr
          popisek="Trenéři"
          hodnota={summary.treneri}
          ikona={Dumbbell}
          ton="znacka"
          zapnuto={filter === "treneri"}
          onClick={() => setFilter("treneri")}
        />
        <DlazdiceFiltr
          popisek="Zaměstnanci"
          hodnota={summary.zamestnanci}
          ikona={TrendingUp}
          ton="ok"
          zapnuto={filter === "ostatni"}
          onClick={() => setFilter("ostatni")}
        />
      </div>

      {/* Zapínátko na ukončené s nulou (rozhodnutí F1). */}
      {ukoncenychSNulou > 0 && (
        <Zapinatko
          zapnuto={zobrazitUkoncene}
          onZmena={setZobrazitUkoncene}
          ikona={zobrazitUkoncene ? EyeOff : Eye}
          popisek={`Zobrazit i ukončené s nulou (${ukoncenychSNulou})`}
        />
      )}

      {/* Na telefonu řádky, na počítači tabulka se všemi sloupci (rozhodnutí T1). */}
      <div className="glass overflow-hidden rounded-2xl sm:hidden">
        {loading && <div className="p-3"><SkeletonRows rows={5} /></div>}
        {!loading && filteredRows.length === 0 && (
          <EmptyState icon={Users} title="Žádné osoby" hint="Za tenhle měsíc tu nikdo není, zkus jiné období nebo jiný typ." />
        )}
        {!loading && filteredRows.length > 0 && (
          <div className="divide-y divide-slate-200 px-4">
            {filteredRows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => setDetailId(r.id)}
                className="block w-full py-3 text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="min-w-0 flex-1 text-sm font-bold text-slate-900">{r.display_name}</span>
                  <span className="shrink-0 whitespace-nowrap text-base font-bold tabular-nums text-slate-900">
                    {fmt(r.k_fakturaci ?? r.celkem)} Kč
                  </span>
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {r.typ_spoluprace && <Pilulka>{r.typ_spoluprace}</Pilulka>}
                  {r.pozice && <Pilulka>{r.pozice}</Pilulka>}
                  {r.fix
                    ? <Pilulka>fix {fmt(r.sazba)} Kč</Pilulka>
                    : <Pilulka>{fmt(r.sazba)} Kč za trénink</Pilulka>}
                  {!r.fix && <Pilulka>{r.jednotky} tréninků</Pilulka>}
                  {r.bonusy > 0 && (
                    <span className="rounded-lg bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-800">bonusy {fmt(r.bonusy)}</span>
                  )}
                  {r.srazky > 0 && (
                    <span className="rounded-lg bg-rose-50 px-2 py-0.5 text-xs font-semibold text-rose-800">srážky {fmt(r.srazky)}</span>
                  )}
                  {r.skryte_srazky > 0 && <Pilulka>bez skrytých {fmt(r.bez_skrytych)} Kč</Pilulka>}
                  {jePredikce && r.predikce != null && <Pilulka>odhad {fmt(r.predikce)} Kč</Pilulka>}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {/osv/i.test(r.typ_spoluprace || "") && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); toggleFaktura(r.id, r.faktura_prijata); }}
                      className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${
                        r.faktura_prijata ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"
                      }`}
                    >
                      {r.faktura_prijata ? <><Check className="h-3 w-3" /> faktura</> : "faktura chybí"}
                    </span>
                  )}
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => { e.stopPropagation(); toggleSchvaleno(r.id, r.schvaleno); }}
                    className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-semibold ${
                      r.schvaleno ? "bg-emerald-50 text-emerald-800" : "bg-slate-100 text-slate-600"
                    }`}
                  >
                    {r.schvaleno ? <><Check className="h-3 w-3" /> schváleno</> : "čeká na schválení"}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Tabulka */}
      <div className="hidden overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-slate-500 border-b-2 border-slate-200 bg-slate-50">
                <th className="text-left font-semibold py-3 px-4">Jméno</th>
                <th className="text-left font-semibold py-3 px-2">Spolupráce</th>
                <th className="text-right font-semibold py-3 px-2">Sazba</th>
                <th className="text-right font-semibold py-3 px-2 whitespace-nowrap">Tréninky</th>
                <th className="text-right font-semibold py-3 px-2">Bonusy</th>
                <th className="text-right font-semibold py-3 px-2">Srážky</th>
                <th className="text-right font-semibold py-3 px-4">Celkem</th>
                <th className="text-right font-semibold py-3 px-2 whitespace-nowrap" title="Částka k fakturaci bez skrytých srážek (zpět přičtený bokem + soukromé odečty)">Bez skrytých</th>
                {jePredikce && <th className="text-right font-semibold py-3 px-2 whitespace-nowrap">Predikce ✨</th>}
                <th className="text-center font-semibold py-3 px-3 whitespace-nowrap" title="Odškrtni, když od člověka (OSVČ) máš za daný měsíc fakturu. Žluté „Chybí“ = ještě nemáš.">Faktura</th>
                <th className="text-center font-semibold py-3 px-3">Schváleno</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={jePredikce ? 11 : 10} className="py-10 text-center text-slate-400 text-sm">Načítám…</td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={jePredikce ? 11 : 10} className="py-10 text-center text-slate-400 text-sm">Žádné osoby.</td></tr>
              )}
              {filteredRows.map((r) => (
                <tr key={r.id} onClick={() => setDetailId(r.id)}
                  className="border-b border-slate-100 hover:bg-brand-50/40 cursor-pointer">
                  <td className="py-3 px-4">
                    <div className="font-semibold text-slate-900">{r.display_name}</div>
                    <div className="text-xs text-slate-400">{r.pozice}</div>
                  </td>
                  <td className="py-3 px-2">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-lg bg-slate-100 text-slate-700 text-xs font-medium border border-slate-200">
                      {r.typ_spoluprace || "—"}
                    </span>
                  </td>
                  <td className="py-3 px-2 text-right tabular-nums text-slate-700">
                    {!r.fix
                      ? <span className={r.motivace_strzena ? "text-rose-600 font-semibold" : ""}>{fmt(r.sazba)} Kč{r.motivace_strzena ? " *" : ""}</span>
                      : <span className="text-slate-400 text-xs">fix {fmt(r.sazba)} Kč</span>}
                  </td>
                  <td className="py-3 px-2 text-right tabular-nums text-slate-700">
                    {!r.fix ? r.jednotky : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-3 px-2 text-right tabular-nums text-emerald-700">{r.bonusy ? `+${fmt(r.bonusy)}` : "—"}</td>
                  <td className="py-3 px-2 text-right tabular-nums text-rose-600">{r.srazky ? `−${fmt(r.srazky)}` : "—"}</td>
                  <td className="py-3 px-4 text-right tabular-nums font-bold text-slate-900">{fmt(r.k_fakturaci ?? r.celkem)} Kč</td>
                  <td className="py-3 px-2 text-right tabular-nums">
                    {r.skryte_srazky ? (
                      <div className="flex flex-col items-end leading-tight" title={`K fakturaci bez skrytých srážek: ${fmt(r.bez_skrytych)} Kč (skryto −${fmt(r.skryte_srazky)} Kč, bokem + soukromé odečty).`}>
                        <span className="font-semibold text-slate-700">{fmt(r.bez_skrytych)} Kč</span>
                        <span className="text-[11px] text-amber-600">skryto −{fmt(r.skryte_srazky)}</span>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  {jePredikce && (
                    <td className="py-3 px-2 text-right tabular-nums">
                      {r.predikce != null ? (
                        <span
                          className="text-brand-700 font-semibold"
                          title={!r.fix
                            ? `Odhad konce měsíce: odtrénováno ${r.jednotky} + naplánováno ${r.planovane_jednotky || 0} tréninků v Reserviu × sazba`
                            : "Fixní odměna, predikce odpovídá částce Celkem"}
                        >
                          ≈ {fmt(r.predikce)} Kč
                        </span>
                      ) : <span className="text-slate-300" title="Pro tento typ odměny (hodiny/sales) predikci neumíme, záleží na výkazu">—</span>}
                    </td>
                  )}
                  <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {/osv/i.test(r.typ_spoluprace || "") ? (
                      <button onClick={() => toggleFaktura(r.id, r.faktura_prijata)}
                        title={r.faktura_prijata ? "Faktura přijata, klikni pro zrušení" : "Fakturu ještě nemáš, klikni až přijde"}
                        className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-xl border ${r.faktura_prijata ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-amber-50 text-amber-700 border-amber-300 hover:bg-amber-100"}`}>
                        {r.faktura_prijata ? <><Check className="w-3.5 h-3.5" /> Faktura</> : "Chybí"}
                      </button>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="py-3 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => toggleSchvaleno(r.id, r.schvaleno)}
                      className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-xl border ${r.schvaleno ? "bg-emerald-100 text-emerald-700 border-emerald-300" : "bg-white text-slate-500 border-slate-300 hover:bg-slate-50"}`}>
                      {r.schvaleno ? <><Check className="w-3.5 h-3.5" /> Schváleno</> : "Schválit"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailId && (
        <DetailModal mesic={mesic} personId={detailId} onClose={() => setDetailId(null)} onChanged={refetch} />
      )}
    </div>
  );
}

function DetailModal({ mesic, personId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [motivaceForm, setMotivaceForm] = useState(false);
  const [duvod, setDuvod] = useState("");
  const [saving, setSaving] = useState(false);
  // Rucni polozky (owner si hraje)
  const [addOpen, setAddOpen] = useState(false);
  const [nTyp, setNTyp] = useState("bonus");
  const [nPopis, setNPopis] = useState("");
  const [nCastka, setNCastka] = useState("");
  const [itemBusy, setItemBusy] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await apiFetch(`/api/hr-vyplaty?mesic=${mesic}&personId=${encodeURIComponent(personId)}`);
      const j = await res.json();
      setData(j);
    } catch (e) { setData({ error: e.message }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mesic, personId]);

  const row = data?.row;
  const detail = data?.detail;
  const sazbaInfo = data?.sazbaInfo;
  const motivace = detail?.motivace;

  async function setMotivace(strzena) {
    if (strzena && !duvod.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch("/api/hr-vyplaty", {
        method: "POST",
        body: JSON.stringify({ action: "set-motivace", personId, mesic, strzena, duvod: duvod.trim() }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setMotivaceForm(false); setDuvod("");
      await load();
      onChanged?.();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setSaving(false); }
  }

  async function addItem() {
    const castkaNum = parseFloat(String(nCastka).replace(/\s/g, "").replace(",", "."));
    if (!nPopis.trim() || !Number.isFinite(castkaNum) || castkaNum <= 0) return;
    setItemBusy(true);
    try {
      // Owner pridava polozku rovnou do vyplaty daneho mesice → stav vyplacen + mesic_vyplaceni,
      // aby se hned zapocetla (na rozdil od cekajicich bonusu z workflow).
      const res = await apiFetch("/api/hr-bs-add", {
        method: "POST",
        body: JSON.stringify({
          personId, typ: nTyp, popis: nPopis.trim(), castka: castkaNum,
          datum_vzniku: `${mesic}-15`, stav: "vyplacen", mesic_vyplaceni: mesic,
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setNPopis(""); setNCastka(""); setAddOpen(false);
      await load(); onChanged?.();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setItemBusy(false); }
  }

  async function deleteItem(id) {
    if (!await confirmDialog("Smazat položku?")) return;
    setItemBusy(true);
    try {
      const res = await apiFetch(`/api/hr-bs-delete?id=${id}`, { method: "DELETE" });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      await load(); onChanged?.();
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setItemBusy(false); }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      plnaHlavicka
      header={
        <HlavickaOkna
          ikona={Receipt}
          nazev={row?.display_name || "Detail"}
          odznaky={
            <>
              {row?.pozice && <OdznakOkna>{row.pozice}</OdznakOkna>}
              {row?.typ_spoluprace && <OdznakOkna>{row.typ_spoluprace}</OdznakOkna>}
              <OdznakOkna>{mesicLabel(mesic)}</OdznakOkna>
            </>
          }
          onClose={onClose}
        />
      }
    >
        {loading && <SkeletonRows rows={5} />}
        {!loading && data?.error && <ErrorNote>{data.error}</ErrorNote>}

        {!loading && row && (
          <div className="space-y-5">
            {/* Souhrn */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {row.rezim === "treninky" && (
                <>
                  <Box label="Sazba / trénink" value={`${fmt(row.sazba)} Kč`} sub={row.motivace_strzena ? "jen základ" : "vč. motivační"} />
                  <Box label="Tréninky" value={row.jednotky} sub="vč. propadlých" />
                </>
              )}
              {row.rezim === "fix" && (
                <Box
                  label="Měsíční odměna"
                  value={`${fmt(row.sazba)} Kč`}
                  sub={row.zaklad_rucni != null ? `ručně za tento měsíc${row.zaklad_rucni_duvod ? ": " + row.zaklad_rucni_duvod : ""}` : "fix"}
                />
              )}
              {row.rezim === "hodiny" && <Box label="Odměna za hodiny" value={`${fmt(row.zakladni_castka)} Kč`} sub="dle Time reportu" />}
              {row.rezim === "sales" && (
                <>
                  <Box label="Hodiny" value={`${fmt(row.hodiny_rozpad?.[0]?.castka || 0)} Kč`} sub={`${row.hodiny_rozpad?.[0]?.hours || 0} h × ${fmt(row.sazba)}`} />
                  <Box label="Uzavření" value={`${fmt(row.uzavreni?.castka || 0)} Kč`} sub={`${row.uzavreni?.count || 0} × ${fmt(row.uzavreni?.sazba || 0)}`} />
                </>
              )}
              <Box label="Bonusy" value={`+${fmt(row.bonusy)} Kč`} tone="emerald" />
              <Box label="Srážky" value={`−${fmt(row.srazky)} Kč`} tone="rose" />
            </div>

            {/* 3.9.2026: owner může za konkrétní měsíc přepsat základ odměny.
                Změna fixu v HR se propisuje od aktuálního měsíce dál, tohle je
                jednorázová výjimka jen pro tento měsíc. Vše se loguje. */}
            {row.rezim === "fix" && (
              <RucniZaklad row={row} personId={personId} mesic={mesic} onSaved={() => { load(); onChanged?.(); }} />
            )}

            <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold text-brand-900">{row.is_trener ? "K fakturaci" : "K výplatě"}</span>
                <span className="text-2xl font-black tabular-nums text-brand-900">{fmt(row.k_fakturaci ?? row.celkem)} Kč</span>
              </div>
              {((row.dpp_celkem || 0) > 0 || (row.odecty_celkem || 0) > 0 || (row.bokem_castka || 0) > 0) && (
                <div className="text-xs text-brand-700/80 mt-1.5 text-right">
                  Celková odměna {fmt(row.celkem)}
                  {(row.dpp_celkem || 0) > 0 ? ` − DPP ${fmt(row.dpp_celkem)}` : ""}
                  {(row.odecty_celkem || 0) > 0 ? ` − odečty ${fmt(row.odecty_celkem)}` : ""}
                  {(row.bokem_castka || 0) > 0 ? ` − bokem ${fmt(row.bokem_castka)}` : ""} Kč
                </div>
              )}
            </div>

            {/* Rozpad hodin dle oblasti (rezim hodiny) */}
            {row.rezim === "hodiny" && row.hodiny_rozpad?.length > 0 && (
              <div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Hodiny dle typu práce</div>
                <div className="space-y-1.5">
                  {row.hodiny_rozpad.map((h, i) => (
                    <div key={i} className="flex items-center justify-between text-sm border border-slate-100 rounded-xl px-3 py-2">
                      <span className="text-slate-700">{h.oblast}, {h.hours} h × {fmt(h.rate)} Kč</span>
                      <span className="tabular-nums font-semibold text-slate-900">{fmt(h.castka)} Kč</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Uzavreni klientu (rezim sales) */}
            {row.rezim === "sales" && row.uzavreni && (
              <div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Uzavření ({row.uzavreni.count} × {fmt(row.uzavreni.sazba)} Kč)</div>
                {row.uzavreni.items?.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {row.uzavreni.items.map((u, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">{u.klient}</span>
                    ))}
                  </div>
                ) : <div className="text-xs text-slate-400 italic">Zatím žádná uzavření (klient s uhrazenou první fakturou za spolupráci).</div>}
              </div>
            )}

            {/* Rucni zadani hodin (nez bude Time report) */}
            {(row.rezim === "hodiny" || row.rezim === "sales") && (
              <HoursEditor personId={personId} mesic={mesic} rezim={row.rezim} config={data?.config} hodinyRucni={data?.hodiny_rucni} onSaved={() => { load(); onChanged?.(); }} />
            )}

            {/* Pravidelné položky (každý měsíc) */}
            <PravidelneEditor personId={personId} mesic={mesic} pravidelne={row.pravidelne} onSaved={() => { load(); onChanged?.(); }} />

            {/* DPP (pro účetní) + soukromá částka bokem */}
            <DppBokemEditor personId={personId} mesic={mesic} row={row} onSaved={() => { load(); onChanged?.(); }} />

            {/* Typ odmeny — owner konfiguruje */}
            <ConfigEditor personId={personId} mesic={mesic} config={data?.config} rezim={row.rezim} onSaved={() => { load(); onChanged?.(); }} />

            {/* Motivacni slozka (jen per-trenink trener, ne fix) */}
            {!row.fix && (
              <div className="border border-slate-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
                    Motivační složka sazby
                    <InfoTip text="Plná sazba zahrnuje motivační složku. Owner ji může za měsíc strhnout (platí se jen základ). Důvod (porušení) se zobrazí trenérovi." />
                  </div>
                </div>
                {motivace?.strzena ? (
                  <div className="bg-rose-50 border border-rose-200 rounded-xl p-3 text-sm">
                    <div className="flex items-center gap-1.5 text-rose-800 font-semibold mb-1">
                      <AlertTriangle className="w-4 h-4" /> Motivační složka stržena
                    </div>
                    <div className="text-rose-700">{motivace.duvod}</div>
                    <div className="mt-1 text-xs text-rose-400">{motivace.upravil_by}{motivace.upravil_at ? `, ${formatDate(new Date(motivace.upravil_at))}` : ""}</div>
                    <button onClick={() => setMotivace(false)} disabled={saving}
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-white border border-slate-300 hover:bg-slate-50 disabled:opacity-50">
                      {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />} Vrátit motivační složku
                    </button>
                  </div>
                ) : motivaceForm ? (
                  <div className="space-y-2">
                    <textarea value={duvod} onChange={(e) => setDuvod(e.target.value)} rows={2}
                      placeholder="Důvod stržení (porušení pravidel), trenér ho uvidí"
                      className="field w-full" />
                    <div className="flex items-center gap-2">
                      <button onClick={() => setMotivace(true)} disabled={saving || !duvod.trim()}
                        className="btn btn-danger">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Ban className="w-3.5 h-3.5" />} Potvrdit stržení
                      </button>
                      <button onClick={() => { setMotivaceForm(false); setDuvod(""); }} className="text-xs font-medium px-3 py-1.5 rounded-xl border border-slate-300 hover:bg-slate-50">Zrušit</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setMotivaceForm(true)}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-xl bg-white border border-slate-300 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200">
                    <Ban className="w-3.5 h-3.5" /> Strhnout motivační složku
                  </button>
                )}
                {sazbaInfo?.mesicSpoluprace && (
                  <div className="text-xs text-slate-400 mt-2">{sazbaInfo.mesicSpoluprace}. měsíc spolupráce, základ {fmt(sazbaInfo.zaklad)} + motivační {fmt(sazbaInfo.bonus)} Kč</div>
                )}
              </div>
            )}

            {/* Polozky — bonusy, srazky, priplatky (owner muze pridavat/mazat) */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-slate-900">Položky (bonusy, srážky, příplatky)</div>
                {!addOpen && (
                  <button onClick={() => setAddOpen(true)} className="btn btn-primary">
                    <Plus className="w-3.5 h-3.5" /> Přidat položku
                  </button>
                )}
              </div>

              {addOpen && (
                <div className="border border-brand-200 bg-brand-50/40 rounded-xl p-3 mb-2 space-y-2">
                  <div className="flex gap-2">
                    <button onClick={() => setNTyp("bonus")} className={`flex-1 text-xs font-semibold py-1.5 rounded-xl border ${nTyp === "bonus" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-slate-500 border-slate-200"}`}>+ Bonus / příplatek</button>
                    <button onClick={() => setNTyp("srazka")} className={`flex-1 text-xs font-semibold py-1.5 rounded-xl border ${nTyp === "srazka" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-white text-slate-500 border-slate-200"}`}>− Srážka / pokuta</button>
                  </div>
                  <input type="text" value={nPopis} onChange={(e) => setNPopis(e.target.value)} placeholder="Popis (např. Bonus za uzavření)"
                    className="field w-full" />
                  <div className="flex gap-2">
                    <input type="text" inputMode="numeric" value={nCastka} onChange={(e) => setNCastka(e.target.value)} placeholder="Částka Kč"
                      className="field flex-1" />
                    <button onClick={addItem} disabled={itemBusy || !nPopis.trim() || !nCastka}
                      className="btn btn-primary">
                      {itemBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Přidat
                    </button>
                    <button onClick={() => { setAddOpen(false); setNPopis(""); setNCastka(""); }} className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-300 hover:bg-slate-50">Zrušit</button>
                  </div>
                </div>
              )}

              {detail?.polozky?.length > 0 ? (
                <div className="space-y-1.5">
                  {detail.polozky.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-xl px-3 py-2 group">
                      <div>
                        {/* 3. 11. 2026: název bez jména klientky, klientka v pilulce (i u starších záznamů se jménem v textu). */}
                        <span className={`font-medium ${p.typ === "bonus" ? "text-emerald-700" : "text-rose-600"}`}>{p.nazev || p.popis}</span>
                        {(p.klient || p.klient_name) && <span className="ml-1.5 select-none rounded-lg bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600">{p.klient || p.klient_name}</span>}
                        <div className="text-xs text-slate-400">{p.datum}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`tabular-nums font-semibold ${p.typ === "bonus" ? "text-emerald-700" : "text-rose-600"}`}>
                          {p.typ === "bonus" ? "+" : "−"}{fmt(p.castka)} Kč
                        </span>
                        <button onClick={() => deleteItem(p.id)} disabled={itemBusy} title="Smazat položku"
                          className="p-1 rounded text-slate-300 hover:text-rose-600 hover:bg-rose-50 opacity-0 group-hover:opacity-100 transition">
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : <div className="text-xs text-slate-400 italic">Žádné položky v tomto měsíci.</div>}
            </div>

            {/* Treninky (jen per-trenink trener, ne fix) */}
            {!row.fix && (
              <div>
                <div className="text-sm font-semibold text-slate-900 mb-2">Odtrénované tréninky ({detail?.treninky?.length || 0})</div>
                {detail?.treninky?.length > 0 ? (
                  <div className="max-h-64 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-100">
                    {detail.treninky.map((t, i) => (
                      <div key={i} className="flex items-center justify-between text-sm px-3 py-1.5">
                        <span className="text-slate-700">{t.datum}, {t.klient}</span>
                        {t.propadle && <span className="text-xs px-2 py-0.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200">propadlé</span>}
                      </div>
                    ))}
                  </div>
                ) : <div className="text-sm text-slate-400 italic">Žádné tréninky v tomto měsíci.</div>}
              </div>
            )}
          </div>
        )}
    </Modal>
  );
}

function Box({ label, value, sub, tone }) {
  const tones = { emerald: "text-emerald-700", rose: "text-rose-600" };
  return (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${tones[tone] || "text-slate-900"}`}>{value}</div>
      {sub && <div className="text-xs text-slate-400">{sub}</div>}
    </div>
  );
}

const REZIM_LABEL = {
  treninky: "Tréninky × sazba", fix: "Fix měsíčně", hodiny: "Hodiny × sazba",
  sales: "Hodiny + uzavření",
  // 16.9.2026: trenér bootcampů, pevná částka za jednu proběhlou lekci.
  bootcamp: "Bootcamp lekce × sazba",
};

// Pravidelne (mesicne opakujici) polozky — owner pridava/edituje/maze.
function PravidelneEditor({ personId, mesic, pravidelne, onSaved }) {
  const items = pravidelne || [];
  const [addOpen, setAddOpen] = useState(false);
  const [typ, setTyp] = useState("bonus");
  const [popis, setPopis] = useState("");
  const [castka, setCastka] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editCastka, setEditCastka] = useState("");

  async function post(action, payload) {
    setBusy(true);
    try {
      // mesic posilame vzdy: server z nej urci, od kdy zmena plati (nikdy zpetne).
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action, mesic, ...payload }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || res.status);
      setAddOpen(false); setPopis(""); setCastka(""); setEditId(null);
      onSaved?.();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3">
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm font-semibold text-slate-900">Pravidelné položky (každý měsíc)</div>
        {!addOpen && <button onClick={() => setAddOpen(true)} className="btn btn-primary"><Plus className="w-3.5 h-3.5" /> Přidat</button>}
      </div>
      {addOpen && (
        <div className="border border-brand-200 bg-brand-50/40 rounded-xl p-3 mb-2 space-y-2">
          <div className="flex gap-2">
            <button onClick={() => setTyp("bonus")} className={`flex-1 text-xs font-semibold py-1.5 rounded-xl border ${typ === "bonus" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-white text-slate-500 border-slate-200"}`}>+ Bonus</button>
            <button onClick={() => setTyp("srazka")} className={`flex-1 text-xs font-semibold py-1.5 rounded-xl border ${typ === "srazka" ? "bg-rose-100 text-rose-800 border-rose-300" : "bg-white text-slate-500 border-slate-200"}`}>− Srážka</button>
          </div>
          <input type="text" value={popis} onChange={(e) => setPopis(e.target.value)} placeholder="Popis (např. Asistence Petra)" className="field w-full" />
          <div className="flex gap-2">
            <input type="text" inputMode="numeric" value={castka} onChange={(e) => setCastka(e.target.value)} placeholder="Částka Kč" className="field flex-1" />
            <button onClick={() => post("add-pravidelne", { personId, typ, popis: popis.trim(), castka })} disabled={busy || !popis.trim() || !castka} className="btn btn-primary">Přidat</button>
            <button onClick={() => { setAddOpen(false); setPopis(""); setCastka(""); }} className="px-3 py-2 text-sm font-medium rounded-xl border border-slate-300">Zrušit</button>
          </div>
        </div>
      )}
      {items.length > 0 ? (
        <div className="space-y-1.5">
          {items.map((p) => (
            <div key={p.id} className="flex items-center justify-between text-sm border border-slate-100 rounded-xl px-3 py-2 group">
              <span className={`font-medium ${p.typ === "bonus" ? "text-emerald-700" : "text-rose-600"}`}>{p.popis}</span>
              <div className="flex items-center gap-2">
                {editId === p.id ? (
                  <>
                    <input type="text" inputMode="numeric" value={editCastka} onChange={(e) => setEditCastka(e.target.value)} className="field w-24 text-right" />
                    <button onClick={() => post("update-pravidelne", { id: p.id, castka: editCastka })} disabled={busy} className="text-xs font-semibold text-brand-700">Uložit</button>
                    <button onClick={() => setEditId(null)} className="text-xs text-slate-400">Zrušit</button>
                  </>
                ) : (
                  <>
                    <span className={`tabular-nums font-semibold ${p.typ === "bonus" ? "text-emerald-700" : "text-rose-600"}`}>{p.typ === "bonus" ? "+" : "−"}{fmt(p.castka)} Kč</span>
                    <button onClick={() => { setEditId(p.id); setEditCastka(String(p.castka)); }} title="Upravit částku" className="text-slate-300 hover:text-brand-600 opacity-0 group-hover:opacity-100"><Pencil className="w-3.5 h-3.5" /></button>
                    <button onClick={async () => { if (await confirmDialog("Smazat pravidelnou položku?")) post("delete-pravidelne", { id: p.id }); }} title="Smazat" className="text-slate-300 hover:text-rose-600 opacity-0 group-hover:opacity-100"><X className="w-3.5 h-3.5" /></button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : <div className="text-xs text-slate-400 italic">Žádné pravidelné položky.</div>}
    </div>
  );
}

// Ruční částka za konkrétní měsíc (3.9.2026). Fix v HR platí od měsíce dál,
// tohle je jednorázová výjimka jen pro tenhle měsíc. Historie změn se ukládá
// do hr_vyplata_zmeny, takže je vždy vidět kdo, kdy a z čeho na co.
function RucniZaklad({ row, personId, mesic, onSaved }) {
  const [otevreno, setOtevreno] = useState(false);
  const [castka, setCastka] = useState(row.zaklad_rucni != null ? String(row.zaklad_rucni) : "");
  const [duvod, setDuvod] = useState(row.zaklad_rucni_duvod || "");
  const [busy, setBusy] = useState(false);

  async function uloz(vymazat) {
    setBusy(true);
    try {
      const res = await apiFetch("/api/hr-vyplaty", {
        method: "POST",
        body: JSON.stringify({
          action: "set-zaklad", personId, mesic,
          castka: vymazat ? "" : castka,
          duvod: vymazat ? "" : duvod.trim(),
        }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setOtevreno(false);
      onSaved?.();
      toast(vymazat ? "Ruční částka zrušena" : "Částka uložena", "success");
    } catch (e) { toast(`Chyba: ${e.message}`, "error"); }
    finally { setBusy(false); }
  }

  if (!otevreno) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
        <div className="min-w-0 text-sm">
          <div className="font-semibold text-slate-800">Částka za tento měsíc</div>
          <div className="text-xs text-slate-500">
            {row.zaklad_rucni != null
              ? `Ručně ${fmt(row.zaklad_rucni)} Kč místo ${fmt(row.zaklad_puvodni)} Kč`
              : "Počítá se z fixu platného pro tento měsíc"}
          </div>
        </div>
        <button onClick={() => setOtevreno(true)}
          className="shrink-0 px-3 py-1.5 text-sm font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50">
          Upravit
        </button>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-brand-200 bg-brand-50/60 px-4 py-3 space-y-2">
      <div className="text-sm font-semibold text-brand-900">Ruční částka jen pro {mesicLabel(mesic)}</div>
      <div className="flex flex-wrap gap-2">
        <input type="text" inputMode="numeric" value={castka} onChange={(e) => setCastka(e.target.value)}
          placeholder={`Základ v Kč (teď ${fmt(row.zaklad_puvodni)})`}
          className="field w-40" />
        <input type="text" value={duvod} onChange={(e) => setDuvod(e.target.value)}
          placeholder="Důvod (nástup v půlce měsíce, dohoda…)"
          className="field flex-1 min-w-[12rem]" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => uloz(false)} disabled={busy || !castka.trim()}
          className="btn btn-primary">Uložit</button>
        {row.zaklad_rucni != null && (
          <button onClick={() => uloz(true)} disabled={busy}
            className="px-3 py-2 text-sm font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50">Zrušit ruční částku</button>
        )}
        <button onClick={() => setOtevreno(false)} disabled={busy}
          className="px-3 py-2 text-sm font-semibold rounded-xl text-slate-600 hover:bg-white">Zavřít</button>
      </div>
    </div>
  );
}

// DPP (kanal pro uctetni) + soukroma castka "bokem" (vidi jen owner).
function DppBokemEditor({ personId, mesic, row, onSaved }) {
  const [dppPolozky, setDppPolozky] = useState(() => {
    if (Array.isArray(row.dpp_polozky) && row.dpp_polozky.length) return row.dpp_polozky.map((p) => ({ popis: p.popis || "", castka: String(p.castka ?? ""), hodiny: p.hodiny != null ? String(p.hodiny) : "" }));
    const init = [];
    if ((row.dpp_castka || 0) > 0) init.push({ popis: "Hodinovka", castka: String(row.dpp_castka), hodiny: row.dpp_hodiny != null ? String(row.dpp_hodiny) : "" });
    if ((row.dpp_bonus_castka || 0) > 0) init.push({ popis: "Bonus", castka: String(row.dpp_bonus_castka), hodiny: "" });
    return init;
  });
  const [odecty, setOdecty] = useState(Array.isArray(row.odecty) ? row.odecty.map((o) => ({ popis: o.popis || "", castka: String(o.castka ?? ""), opakovat: o.opakovat === true, verejny: o.verejny === true })) : []);
  const [busy, setBusy] = useState("");
  const [saved, setSaved] = useState("");

  async function post(action, payload, key) {
    setBusy(key);
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action, personId, mesic, ...payload }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || res.status);
      setSaved(key);
      setTimeout(() => setSaved(""), 2500);
      onSaved?.();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    finally { setBusy(""); }
  }

  const dppUlozeno = (row.dpp_celkem || 0) > 0;
  const setDppItem = (i, field, val) => setDppPolozky(dppPolozky.map((p, idx) => (idx === i ? { ...p, [field]: val } : p)));
  const addDpp = () => setDppPolozky([...dppPolozky, { popis: "", castka: "", hodiny: "" }]);
  const removeDpp = (i) => setDppPolozky(dppPolozky.filter((_, idx) => idx !== i));
  async function saveDpp() {
    const clean = dppPolozky.filter((p) => p.castka).map((p) => ({ popis: String(p.popis).trim() || "DPP", castka: p.castka, hodiny: p.hodiny }));
    await post("set-dpp", { polozky: clean }, "dpp");
  }
  const odectyUlozeno = (row.odecty_celkem || 0) > 0;
  // V uzavřeném měsíci se opakované řádky needitují, úprava platí jen pro ten měsíc.
  // Zaškrtávátko Opakovat by tam jen mátlo, proto se schová (1.10.2026).
  const uzavrenyMesic = mesic < currentMonth();
  const setOdItem = (i, field, val) => setOdecty(odecty.map((o, idx) => (idx === i ? { ...o, [field]: val } : o)));
  const addOd = () => setOdecty([...odecty, { popis: "", castka: "", opakovat: false, verejny: false }]);
  const removeOd = (i) => setOdecty(odecty.filter((_, idx) => idx !== i));
  async function saveOdecty() {
    const clean = odecty.filter((o) => String(o.popis).trim() && o.castka).map((o) => ({ popis: String(o.popis).trim(), castka: o.castka, opakovat: o.opakovat === true, verejny: o.verejny === true }));
    await post("set-odecty", { odecty: clean }, "odecty");
  }

  return (
    <div className="space-y-3">
      {/* DPP */}
      <div className={`border rounded-xl p-3 ${dppUlozeno ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-slate-900">DPP (pro účetní)</div>
          {dppUlozeno && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full"><Check className="w-3 h-3" /> Uloženo {fmt(row.dpp_celkem)} Kč</span>}
        </div>
        <div className="space-y-2">
          {dppPolozky.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="text" value={p.popis} onChange={(e) => setDppItem(i, "popis", e.target.value)} placeholder="Popis (např. Hodinovka, Bonus)" className="field flex-1" />
              <input type="text" inputMode="decimal" value={p.hodiny} onChange={(e) => setDppItem(i, "hodiny", e.target.value)} placeholder="hod" className="field w-16 text-right" />
              <input type="text" inputMode="numeric" value={p.castka} onChange={(e) => setDppItem(i, "castka", e.target.value)} placeholder="Kč" className="field w-24 text-right" />
              <button onClick={() => removeDpp(i)} className="p-1 text-slate-300 hover:text-rose-600" title="Odebrat řádek"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button onClick={addDpp} className="btn btn-secondary"><Plus className="w-3.5 h-3.5" /> Přidat řádek</button>
            <button onClick={saveDpp} disabled={busy === "dpp"}
              className={`btn ${saved === "dpp" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "btn-primary"}`}>
              {busy === "dpp" ? <Loader2 className="w-4 h-4 animate-spin" /> : saved === "dpp" ? <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Uloženo</span> : "Uložit DPP"}
            </button>
          </div>
        </div>
        <div className="text-xs text-slate-400 mt-1">Každý řádek jde do exportu DPP zvlášť (popis, hodiny, částka).</div>
      </div>

      {/* Odecty — soukrome radky co snizuji k fakturaci (napr. fakturace bokem) */}
      <div className={`border rounded-xl p-3 ${odectyUlozeno ? "border-amber-400 bg-amber-50/70" : "border-amber-200 bg-amber-50/40"}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-semibold text-amber-900 flex items-center gap-1.5"><EyeOff className="w-4 h-4" /> Odečty / vyplaceno bokem (jen ty)</div>
          {odectyUlozeno && <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full"><Check className="w-3 h-3" /> Uloženo −{fmt(row.odecty_celkem)} Kč{row.odecty_pravidelne ? ", pravidelně" : ""}</span>}
        </div>
        <div className="space-y-2">
          {odecty.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <input type="text" value={o.popis} onChange={(e) => setOdItem(i, "popis", e.target.value)} placeholder="Popis (např. fakturace klient X)" className="field flex-1" />
              <input type="text" inputMode="numeric" value={o.castka} onChange={(e) => setOdItem(i, "castka", e.target.value)} placeholder="Kč" className="field w-28 text-right" />
              {!uzavrenyMesic && (
                <label className="flex items-center gap-1 text-xs text-amber-900 cursor-pointer whitespace-nowrap select-none" title="Opakovat každý měsíc">
                  <input type="checkbox" checked={o.opakovat === true} onChange={(e) => setOdItem(i, "opakovat", e.target.checked)} className="w-4 h-4 rounded border-amber-400" />
                  Opakovat
                </label>
              )}
              {uzavrenyMesic && o.opakovat === true && (
                <span className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800" title="Opakovaný řádek. Změna tady platí jen pro tenhle měsíc.">Opakovaný</span>
              )}
              <label className="flex items-center gap-1 text-xs text-emerald-800 cursor-pointer whitespace-nowrap select-none" title="Zobrazit tento řádek zaměstnanci v Moje výplata">
                <input type="checkbox" checked={o.verejny === true} onChange={(e) => setOdItem(i, "verejny", e.target.checked)} className="w-4 h-4 rounded border-emerald-400" />
                Zobrazit
              </label>
              <button onClick={() => removeOd(i)} className="p-1 text-amber-400 hover:text-rose-600" title="Odebrat řádek"><X className="w-4 h-4" /></button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <button onClick={addOd} className="btn btn-secondary border-amber-300 text-amber-800"><Plus className="w-3.5 h-3.5" /> Přidat řádek</button>
            <button onClick={saveOdecty} disabled={busy === "odecty"}
              className={`btn ${saved === "odecty" ? "bg-emerald-600 text-white hover:bg-emerald-700" : "bg-amber-600 text-white hover:bg-amber-700"}`}>
              {busy === "odecty" ? <Loader2 className="w-4 h-4 animate-spin" /> : saved === "odecty" ? <span className="inline-flex items-center gap-1"><Check className="w-4 h-4" /> Uloženo</span> : "Uložit odečty"}
            </button>
          </div>
        </div>
        <div className="text-xs text-amber-600 mt-1">Soukromé. Každý řádek snižuje částku k fakturaci. Zaškrtni „Opakovat" u řádku, který má chodit každý měsíc. Zaměstnanec řádky nevidí.</div>
        <div className="text-xs text-amber-600/80 mt-0.5">
          {uzavrenyMesic
            ? "Uzavřený měsíc: změna platí jen pro tenhle měsíc, pravidelné řádky zůstanou pro další měsíce stejné."
            : "Změna opakovaného řádku platí od tohoto měsíce dál, uzavřené měsíce zůstanou beze změny."}
        </div>
      </div>
    </div>
  );
}

// Owner zadava rucne pocet hodin po cinnostech (nez bude Time report).
function HoursEditor({ personId, mesic, rezim, config, hodinyRucni, onSaved }) {
  const isSales = rezim === "sales";
  const oblasti = isSales ? ["hodiny"] : Object.keys(config?.rates || {});
  const [vals, setVals] = useState(() => {
    const init = {};
    const src = hodinyRucni || {};
    for (const o of oblasti) {
      let v = src[o];
      if (v == null) { const found = Object.keys(src).find((k) => k.toLowerCase().trim() === o.toLowerCase().trim()); if (found) v = src[found]; }
      init[o] = v != null ? String(v) : "";
    }
    return init;
  });
  const [busy, setBusy] = useState(false);

  async function save() {
    const hodiny = {};
    for (const o of oblasti) { const v = parseFloat(String(vals[o]).replace(",", ".")); if (Number.isFinite(v) && v > 0) hodiny[o] = v; }
    setBusy(true);
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action: "set-hodiny", personId, mesic, hodiny }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      onSaved?.();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3">
      <div className="text-sm font-semibold text-slate-900 mb-2">Hodiny za {mesicLabel(mesic)}{!isSales ? " (dle činnosti)" : ""}</div>
      {(isSales || oblasti.length > 0) ? (
        <div className="space-y-2">
          {oblasti.map((o) => (
            <label key={o} className="flex items-center justify-between gap-2 text-sm">
              <span className="text-slate-600">{isSales ? "Počet hodin" : o}{!isSales && config?.rates?.[o] ? ` (${fmt(config.rates[o])} Kč/h)` : ""}</span>
              <input type="text" inputMode="decimal" value={vals[o]} onChange={(e) => setVals({ ...vals, [o]: e.target.value })}
                className="field w-28 text-right" placeholder="0" />
            </label>
          ))}
          <button onClick={save} disabled={busy} className="btn btn-primary">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Uložit hodiny
          </button>
        </div>
      ) : <div className="text-xs text-slate-400 italic">Nejdřív přidej činnosti se sazbami v „Typ odměny" (např. admin=160).</div>}
      <div className="text-xs text-slate-400 mt-2">Ruční zadání. Až bude Time report, hodiny se vezmou z něj.</div>
    </div>
  );
}

// Owner konfiguruje typ odmeny osoby (treninky/fix/hodiny/sales/bootcamp).
function ConfigEditor({ personId, mesic, config, rezim, onSaved }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(config?.mode || rezim || "treninky");
  const [fixVal, setFixVal] = useState(config?.fix != null ? String(config.fix) : "");
  const [hourly, setHourly] = useState(config?.hourly != null ? String(config.hourly) : "200");
  const [perClose, setPerClose] = useState(config?.per_close != null ? String(config.per_close) : "200");
  const [perLekce, setPerLekce] = useState(config?.per_lekce != null ? String(config.per_lekce) : "500");
  // Přípočet za bootcampovou lekci u trenéra placeného za tréninky (16.9.2026).
  const [bcPripocet, setBcPripocet] = useState(config?.bootcamp_per_lekce != null ? String(config.bootcamp_per_lekce) : "");
  const [defRate, setDefRate] = useState(config?.default != null ? String(config.default) : "");
  const [ratesText, setRatesText] = useState(
    config?.rates ? Object.entries(config.rates).map(([k, v]) => `${k}=${v}`).join("\n") : ""
  );
  const [busy, setBusy] = useState(false);

  async function save() {
    let cfg = { mode };
    if (mode === "fix") cfg.fix = parseInt(fixVal, 10) || 0;
    else if (mode === "sales") { cfg.hourly = parseInt(hourly, 10) || 0; cfg.per_close = parseInt(perClose, 10) || 0; }
    else if (mode === "bootcamp") cfg.per_lekce = parseInt(perLekce, 10) || 0;
    if (mode === "treninky" && String(bcPripocet).trim() !== "") cfg.bootcamp_per_lekce = parseInt(bcPripocet, 10) || 0;
    else if (mode === "hodiny") {
      const rates = {};
      for (const line of ratesText.split("\n")) {
        const [k, v] = line.split("=");
        if (k && k.trim() && v) rates[k.trim()] = parseInt(v, 10) || 0;
      }
      cfg.rates = rates;
      cfg.default = parseInt(defRate, 10) || 0;
    }
    // treninky → prazdny config (jen mode)
    setBusy(true);
    try {
      const res = await apiFetch("/api/hr-vyplaty", { method: "POST", body: JSON.stringify({ action: "set-config", personId, mesic, config: cfg }) });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error || `HTTP ${res.status}`);
      setOpen(false); onSaved?.();
    } catch (e) { toast("Chyba: " + e.message, "error"); }
    finally { setBusy(false); }
  }

  return (
    <div className="border border-slate-200 rounded-xl p-3">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">Typ odměny: <span className="font-semibold text-slate-900">{REZIM_LABEL[rezim] || rezim}</span></div>
        <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-brand-700 hover:underline">{open ? "Zavřít" : "Změnit"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {["treninky", "fix", "hodiny", "sales", "bootcamp"].map((m) => (
              <button key={m} onClick={() => setMode(m)} className={`text-xs font-semibold py-1.5 rounded-xl border ${mode === m ? "bg-brand-100 text-brand-800 border-brand-300" : "bg-white text-slate-500 border-slate-200"}`}>{REZIM_LABEL[m]}</button>
            ))}
          </div>
          {mode === "fix" && (
            <label className="block text-sm"><span className="text-slate-600">Fix měsíčně (Kč)</span>
              <input type="text" inputMode="numeric" value={fixVal} onChange={(e) => setFixVal(e.target.value)} className="field w-full mt-1" placeholder="40000" /></label>
          )}
          {mode === "treninky" && (
            <label className="block text-sm"><span className="text-slate-600">Navíc za bootcampovou lekci (Kč)</span>
              <input type="text" inputMode="numeric" value={bcPripocet} onChange={(e) => setBcPripocet(e.target.value)} className="field w-full mt-1" placeholder="prázdné = bootcampy nevede" />
              <span className="mt-1 block text-xs text-slate-500">
                Pro trenéra, který občas zaskočí na bootcampu. Osobní tréninky se počítají dál po svém.
              </span>
            </label>
          )}
          {mode === "bootcamp" && (
            <label className="block text-sm"><span className="text-slate-600">Za jednu lekci bootcampu (Kč)</span>
              <input type="text" inputMode="numeric" value={perLekce} onChange={(e) => setPerLekce(e.target.value)} className="field w-full mt-1" placeholder="500" />
              <span className="mt-1 block text-xs text-slate-500">
                Počítají se jen lekce, které opravdu proběhly. Zrušená lekce se neproplácí.
              </span>
            </label>
          )}
          {mode === "sales" && (
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm"><span className="text-slate-600">Sazba / hod</span>
                <input type="text" inputMode="numeric" value={hourly} onChange={(e) => setHourly(e.target.value)} className="field w-full mt-1" /></label>
              <label className="block text-sm"><span className="text-slate-600">Za uzavření</span>
                <input type="text" inputMode="numeric" value={perClose} onChange={(e) => setPerClose(e.target.value)} className="field w-full mt-1" /></label>
            </div>
          )}
          {mode === "hodiny" && (
            <div className="space-y-2">
              <label className="block text-sm"><span className="text-slate-600">Sazby dle oblasti (řádek = oblast=sazba)</span>
                <textarea value={ratesText} onChange={(e) => setRatesText(e.target.value)} rows={3} className="field w-full mt-1 font-mono" placeholder={"admin=160\nvyziva=230"} /></label>
              <label className="block text-sm"><span className="text-slate-600">Výchozí sazba (ostatní oblasti)</span>
                <input type="text" inputMode="numeric" value={defRate} onChange={(e) => setDefRate(e.target.value)} className="field w-full mt-1" placeholder="230" /></label>
            </div>
          )}
          <button onClick={save} disabled={busy} className="btn btn-primary">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Uložit typ odměny
          </button>
        </div>
      )}
    </div>
  );
}
